// Results sessions, one per exam or homework filmstrip tab: which panel tab, which student.
// The tab id is the đề's `?unit=`; while a homework student is open the board shows `view` instead.
// Import-free, so node --test can load it.

export type ResultKind = 'exam' | 'homework';
export type ResultPanelTab = 'report' | 'detail';
/** Chi tiết's sub-tab: Theo xếp hạng or, for an exam, Theo câu hỏi. */
export type ResultDetailTab = 'students' | 'questions';

export interface ResultSession {
  /** The đề's `?unit=` value and filmstrip tile id. */
  tabId: string;
  kind: ResultKind;
  /** IZ exam id, or the homework task id. */
  refId: string;
  title: string;
  panelTab: ResultPanelTab;
  detailTab: ResultDetailTab;
  /** Student code whose work replaces the đề, or null for the đề itself. */
  student: string | null;
  /** Homework: which part of the submission is shown. */
  item: number;
  /** Bumped on every pick, so choosing the shown part again re-opens a dismissed overlay. */
  tick: number;
  /** The `?unit=` the board shows for this tab while a homework student is open; null on the đề. */
  view: string | null;
}

export type SessionMap = Record<string, ResultSession>;

export interface SessionInit {
  tabId: string;
  kind: ResultKind;
  refId: string;
  title: string;
}

export function newSession(init: SessionInit): ResultSession {
  return { tabId: init.tabId, kind: init.kind, refId: init.refId, title: init.title, panelTab: 'report', detailTab: 'students', student: null, item: 0, tick: 0, view: null };
}

/** Opens the tab's session; re-opening the same exam or task keeps where the teacher was. */
export function openSession(map: SessionMap, init: SessionInit): SessionMap {
  const current = map[init.tabId];
  if (current && current.kind === init.kind && current.refId === init.refId) return map;
  const next: SessionMap = { ...map };
  next[init.tabId] = newSession(init);
  return next;
}

export function closeSession(map: SessionMap, tabId: string): SessionMap {
  if (!map[tabId]) return map;
  const next: SessionMap = { ...map };
  delete next[tabId];
  return next;
}

export function patchSession(map: SessionMap, tabId: string, patch: Partial<ResultSession>): SessionMap {
  const current = map[tabId];
  if (!current) return map;
  const next: SessionMap = { ...map };
  next[tabId] = { ...current, ...patch, tabId: current.tabId };
  return next;
}

/** The tab the board is on: the `?unit=` itself, or the tab whose student view it is. */
export function sessionTabOf(map: SessionMap, unitId: string | null): string | null {
  if (!unitId) return null;
  if (map[unitId]) return unitId;
  const keys = Object.keys(map);
  for (let i = 0; i < keys.length; i += 1) {
    if (map[keys[i]].view === unitId) return keys[i];
  }
  return null;
}

/** The session that follows the board page, or null on a page without one (Bảng trắng, slides). */
export function activeSession(map: SessionMap, unitId: string | null): ResultSession | null {
  const tabId = sessionTabOf(map, unitId);
  return tabId ? map[tabId] : null;
}

/** Where tapping a tile goes: the open student's work, else the tab itself. */
export function sessionTarget(map: SessionMap, tabId: string): string {
  const session = map[tabId];
  return session && session.view ? session.view : tabId;
}

/** The code `delta` steps from `code` in `order`, or null past either end. */
export function stepStudent(order: string[], code: string | null, delta: number): string | null {
  const at = code ? order.indexOf(code) : -1;
  if (at < 0) return null;
  const next = at + delta;
  return next >= 0 && next < order.length ? order[next] : null;
}
